import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import { Tooltip } from '@mui/material';
import { atlasClient } from '../api/atlasClient';
import { ContextChips } from './ContextChips';

export type UpdateType = 'new_element' | 'new_article' | 'article_update' | 'none';

export interface UpdateCardProps {
  type?: UpdateType;
  title: string;
  description: string;
  date?: string;
  image_id?: string;
  resource_fqn: string;
  priority?: 'LOW' | 'MEDIUM' | 'HIGH';
  priority_reasoning?: string;
  context?: Record<string, any>;
  onProfileClick: () => void;
  viewType?: 'items' | 'updates';
  isCompact?: boolean;
  disabled?: boolean;
  isInactive?: boolean;
}

interface TypeConfig {
  icon: React.ReactNode;
  getColor: (priority?: string) => string;
  label: string;
}

const getPriorityColor = (priority?: string): string => {
  switch (priority) {
    case 'LOW':
      return '#4caf50';
    case 'MEDIUM':
      return '#ff9800';
    case 'HIGH':
      return '#f44336';
    default:
      return '#4caf50';
  }
};

const normalizeType = (type?: string): UpdateType => {
  if (!type) return 'none';
  return type.toLowerCase().replace(/_/g, '_') as UpdateType;
};

const typeConfig: Record<UpdateType, TypeConfig> = {
  'new_element': {
    icon: <NotificationsIcon />,
    getColor: () => '#4caf50',
    label: 'New'
  },
  'new_article': {
    icon: <NewReleasesIcon />,
    getColor: () => '#cd332a',
    label: 'New'
  },
  'article_update': {
    icon: <AnalyticsIcon />,
    getColor: getPriorityColor,
    label: 'Updated'
  },
  'none': {
    icon: null,
    getColor: () => 'transparent',
    label: ''
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  } else {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
};

export const UpdateCard: React.FC<UpdateCardProps> = ({
  type,
  title,
  description,
  date,
  image_id,
  resource_fqn,
  priority,
  priority_reasoning,
  context,
  onProfileClick,
  viewType,
  isCompact = false,
  disabled = false,
  isInactive = false
}) => {
  const [heroImageUrl, setHeroImageUrl] = useState<string | null>(null);
  const normalizedType = normalizeType(type);

  useEffect(() => {
    const loadHeroImage = async () => {
      if (image_id) {
        try {
          const url = await atlasClient.getAssetUrl(image_id);
          setHeroImageUrl(url);
        } catch (error) {
          console.error('Failed to load hero image:', error);
          setHeroImageUrl(null);
        }
      }
    };

    loadHeroImage();
  }, [image_id]);

  const formattedDate = date ? formatDate(date) : '';
  const typeLabel = normalizedType ? normalizedType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '';

  const handleClick = () => {
    if (!disabled) {
      onProfileClick();
    }
  };

  if (isCompact) {
    return (
      <div className={`update-card ${disabled ? 'disabled' : ''} ${isInactive ? 'inactive' : ''}`} onClick={handleClick}>
        <div className="card-header">
          <div className="card-type" style={{ backgroundColor: normalizedType ? typeConfig[normalizedType].getColor(priority) : undefined }}>
            {normalizedType && typeConfig[normalizedType].icon}
            {normalizedType ? typeConfig[normalizedType].label : typeLabel}
          </div>
        </div>
        <div className="card-wrapper">
          <div className="card-content">
            <h3 className="card-title">{title}</h3>
            <p className="card-description">{description}</p>
            <div className="card-footer">
              <div className="card-date">{formattedDate}</div>
            </div>
          </div>
          {heroImageUrl && (
            <div className="card-image">
              <img src={heroImageUrl} alt={title} />
            </div>
          )}
        </div>
      </div>
    );
  }

  const config = normalizedType ? typeConfig[normalizedType] : {
    icon: null,
    getColor: () => 'transparent',
    label: ''
  };

  const typeBadge = (
    <div 
      className="type-badge"
      style={{ backgroundColor: config.getColor(priority) }}
    >
      {config.icon}
      {config.label}
    </div>
  );

  return (
    <div 
      className={`update-card ${viewType || ''} ${disabled ? 'disabled' : ''} ${isInactive ? 'inactive' : ''}`}
      onClick={handleClick}
    >
      {heroImageUrl && (
        <div 
          className="hero-image" 
          style={{ backgroundImage: `url(${heroImageUrl})` }}
        />
      )}
      <div className="card-content">
        <div className="card-header">
          {normalizedType && priority_reasoning ? (
            <Tooltip title={priority_reasoning} arrow>
              {typeBadge}
            </Tooltip>
          ) : normalizedType ? typeBadge : null}
          {date && <span className="date">{formattedDate}</span>}
        </div>
        <h3 className="title">{title}</h3>
        {context && Object.keys(context).length > 0 && (
          <ContextChips context={context} />
        )}
        <div className="description">
          <ReactMarkdown>{description}</ReactMarkdown>
        </div>
      </div>
    </div>
  );
};